import {
  Analytics,
  MediaFile,
  PostType
} from '@/lomavisSharedUi/atoms/cards/LomavisPostCard2';
import { LomavisPostCardList1 } from '@/lomavisSharedUi/organisms/lists/LomavisPostCardList1';
import { useTranslate } from '@tolgee/react';
import Select from 'react-select';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import PlaceholderImage from '../../assets/images/placeholders/images/placeholder-image-square.jpg';
import { LomavisPlatformIcon } from '@/lomavisSharedUi/atoms/icons/LomavisPlatformIcon';
import { LomavisUpgradeSubscriptionButton1 } from '@/lomavisSharedUi/atoms/links/LomavisUpgradeSubscriptionButton1';

const REEL = 'reel';
interface Competitor {
  uuid: string;
  platform: string;
  username: string;
  name: string;
  profile_picture: string;
  posts_data: any[];
}

interface Props {
  competitors: Competitor[];
  selectedPlatform: string;
  loadingCompetitors: boolean;
  totalCompetitors: number | null;
  maxCompetitors: number | null;
}
type SortOption = {
  value: string;
  label: string;
};

type FormattedPost = {
  title: string;
  text: string;
  media: MediaFile[];
  postType: PostType;
  type: string;
  publicationDate: string; // ISO string
  platforms: string[];
  analytics: Analytics;
  permalink: string;
  profile: {
    name: string;
    imageUrl: string;
  };
};

const SocialFeeds: React.FC<Props> = ({
  competitors,
  selectedPlatform,
  loadingCompetitors,
  totalCompetitors,
  maxCompetitors
}) => {
  const { t } = useTranslate();
  const [selectedCompetitors, setSelectedCompetitors] = useState<string[]>([
    'all'
  ]);
  const [sortBy, setSortBy] = useState<string>('date-desc');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 🔎 Compute posts_data based on selected competitor
  const options = [
    { value: 'all', label: t('competitorAnalytics.allCompetitors') },
    ...competitors.map((c) => ({
      value: c.name || c.username,
      label: c.name || c.username
    }))
  ];

  // Sorting options
  const sortOptions: SortOption[] = [
    {
      value: 'date-desc',
      label: t('competitorAnalytics.sortByDateDesc')
    },
    {
      value: 'date-asc',
      label: t('competitorAnalytics.sortByDateAsc')
    },
    {
      value: 'impressions-desc',
      label: t('competitorAnalytics.sortByImpressionsDesc')
    },
    {
      value: 'impressions-asc',
      label: t('competitorAnalytics.sortByImpressionsAsc')
    },
    {
      value: 'interactions-desc',
      label: t('competitorAnalytics.sortByInteractionsDesc')
    },
    {
      value: 'interactions-asc',
      label: t('competitorAnalytics.sortByInteractionsAsc')
    }
  ];

  // ✅ Filter posts based on multiple selections
  const filteredPosts = useMemo(() => {
    if (
      selectedCompetitors.includes('all') ||
      selectedCompetitors?.length === 0
    ) {
      return competitors.flatMap((c) =>
        c.posts_data.map((post) => ({
          ...post,
          platform: c.platform,
          name: c.name,
          username: c.username,
          profile_picture: c.profile_picture
        }))
      );
    }

    return competitors
      .filter((c) => selectedCompetitors.includes(c.name || c.username))
      .flatMap((c) =>
        c.posts_data.map((post) => ({
          ...post,
          platform: c.platform,
          name: c.name,
          username: c.username,
          profile_picture: c.profile_picture
        }))
      );
  }, [selectedCompetitors, competitors]);

  // ✅ Sort posts based on selected sorting option
  const sortedPosts = useMemo(() => {
    const formatted = filteredPosts.map(formatPost);

    return [...formatted].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return (
            new Date(b.publicationDate).getTime() -
            new Date(a.publicationDate).getTime()
          );
        case 'date-asc':
          return (
            new Date(a.publicationDate).getTime() -
            new Date(b.publicationDate).getTime()
          );
        case 'impressions-desc':
          return (
            (b.analytics.impressions || 0) - (a.analytics.impressions || 0)
          );
        case 'impressions-asc':
          return (
            (a.analytics.impressions || 0) - (b.analytics.impressions || 0)
          );
        case 'interactions-desc': {
          const aInteractions =
            (a.analytics.comments || 0) +
            (a.analytics.reactions || 0) +
            (a.analytics.shares || 0);
          const bInteractions =
            (b.analytics.comments || 0) +
            (b.analytics.reactions || 0) +
            (b.analytics.shares || 0);
          return bInteractions - aInteractions;
        }
        case 'interactions-asc': {
          const aInteractions =
            (a.analytics.comments || 0) +
            (a.analytics.reactions || 0) +
            (a.analytics.shares || 0);
          const bInteractions =
            (b.analytics.comments || 0) +
            (b.analytics.reactions || 0) +
            (b.analytics.shares || 0);
          return aInteractions - bInteractions;
        }
        default:
          return 0;
      }
    });
  }, [filteredPosts, sortBy]);

  function formatPost(rawPost: any): FormattedPost {
    const safeMedia = Array.isArray(rawPost?.media) ? rawPost.media : [];

    // 👇 decide if this is a reel
    const isReel =
      rawPost?.type === REEL ||
      (typeof rawPost?.permalink === 'string' &&
        rawPost.permalink.toLowerCase().includes('reel'));

    const mediaItems =
      safeMedia.length === 0 && isReel
        ? [
            // fallback item(s) when no media
            {
              id: 'placeholder_1',
              url: rawPost?.image_preview_url, // or any default image
              preview_image: rawPost?.image_preview_url,
              file_type: 'image'
            }
          ]
        : safeMedia.map((m: any, idx: number) => ({
            id: m?.id ?? `media_${idx + 1}`,
            url: m?.url ?? '',
            preview_image: rawPost?.image_preview_url ?? m?.url ?? '',
            file_type: m?.file_type ?? 'image'
          }));

    return {
      title:
        typeof rawPost?.name === 'string' && rawPost.name.trim() !== ''
          ? rawPost.name
          : 'Untitled Post',

      text: typeof rawPost?.caption === 'string' ? rawPost.caption : '',

      media: mediaItems,

      type: isReel ? REEL : rawPost?.type,

      postType:
        rawPost?.postType ??
        (safeMedia.length > 1
          ? 'multiImage'
          : safeMedia[0]?.file_type ?? 'unknown'),

      publicationDate: rawPost?.published ?? new Date().toISOString(),

      platforms: rawPost?.platform ? [rawPost.platform] : [],

      analytics: {
        comments: Number(rawPost?.analytics?.comments) || 0,
        engagement: Number(rawPost?.analytics?.engagement_rate) || 0,
        impressions: Number(rawPost?.analytics?.impressions) || 0,
        interactions: Number(rawPost?.analytics?.interactions) || 0,
        reactions: Number(rawPost?.analytics?.reactions) || 0,
        shares: Number(rawPost?.analytics?.shares) || 0
      },
      permalink: rawPost?.permalink,

      profile: {
        name: rawPost?.profile_name ?? rawPost?.name ?? 'Unknown',
        imageUrl: rawPost?.profile_picture ?? PlaceholderImage
      }
    };
  }

  useEffect(() => {
    setSelectedCompetitors(['all']);
  }, [selectedPlatform]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const navBarHeight =
    document.getElementById('kt_app_header')?.clientHeight + 15 * 2 + 50;
  return (
    <div className="w-100 h-100">
      {!loadingCompetitors ? (
        competitors.length > 0 ? (
          maxCompetitors && totalCompetitors > maxCompetitors ? (
            <div
              style={{
                height: `calc(100vh - ${navBarHeight}px)`
              }}
              className="w-100 d-flex  "
            >
              <div className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center">
                <span className="fs-3 w-50 text-center">
                  {t('lomavisCompetitorAnalysis.exceededCompetitorCount')}
                </span>
                <div className="d-flex gap-3 justify-content-center">
                  {' '}
                  <LomavisUpgradeSubscriptionButton1 />
                </div>
              </div>
            </div>
          ) : (
            <div className="w-100 h-100 d-flex flex-column gap-3">
              {/* Dropdown filter */}
              {/* Multi-select */}

              <div className="d-flex justify-content-between pe-3">
                <div className="d-flex " style={{ zIndex: 3 }}>
                  <Select
                    isMulti
                    options={options}
                    value={options.filter((o) =>
                      selectedCompetitors.includes(o.value)
                    )}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    onChange={(selected) => {
                      const values = (selected ?? []).map((s) => s.value);

                      // Mixed case: "all" + others
                      if (values.includes('all') && values.length > 1) {
                        if (selectedCompetitors.includes('all')) {
                          // Previously ALL was active -> replace ALL with the newly picked items (drop 'all')
                          setSelectedCompetitors(
                            values.filter((v) => v !== 'all')
                          );
                        } else {
                          // Previously specific items were active -> selecting ALL should override others
                          setSelectedCompetitors(['all']);
                        }
                        return;
                      }

                      // Only "all"
                      if (values.includes('all')) {
                        setSelectedCompetitors(['all']);
                        return;
                      }

                      // Only specific items (no "all")
                      setSelectedCompetitors(values);
                    }}
                    classNames={{
                      control: ({ data }) => `border`,
                      // The chips (selected items in input)
                      multiValue: ({ data }) => `fw-semibold rounded`,

                      // Dropdown options
                      option: ({ isSelected, isFocused, data }) =>
                        `fw-bold z-3 ${
                          isSelected ? 'bg-light text-primary ' : ''
                        } ${isFocused ? 'bg-secondary' : ''}`,
                      // Menu (dropdown container)
                      menu: () => `shadow border rounded`
                    }}
                    menuShouldCloseOnSelect={false}
                    menuShouldScrollIntoView={true}
                    placeholder={t('competitorAnalytics.selectCompetitors')}
                  />
                </div>

                {/* insert dropdown sorting here */}
                {/* Sorting Dropdown */}

                <div className="dropdown text-end" ref={dropdownRef}>
                  <button
                    className="btn btn-white btn-sm dropdown-toggle border"
                    type="button"
                    aria-expanded={showDropdown}
                    onClick={() => setShowDropdown((prev) => !prev)}
                  >
                    {sortOptions.find((option) => option.value === sortBy)
                      ?.label || t('competitorAnalytics.sortBy', 'Sort by...')}
                  </button>
                  <ul
                    style={{ zIndex: 3, minWidth: '200px' }}
                    className={`dropdown-menu${showDropdown ? ' show' : ''}`}
                  >
                    {sortOptions.map((item) => (
                      <li key={item.value} className="w-100 p-1">
                        <button
                          className={`dropdown-item${
                            item.value === sortBy ? ' active' : ''
                          }`}
                          type="button"
                          onClick={() => {
                            setSortBy(item.value);
                            setShowDropdown(false);
                          }}
                        >
                          {item.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Example rendering of filtered posts */}
              <div className="d-flex flex-wrap gap-3 ">
                {sortedPosts.length === 0 ? (
                  <div className="w-100 d-flex justify-content-center">
                    <p className="fw-bold">
                      {t('competitorAnalytics.noPostsFound')}
                    </p>
                  </div>
                ) : (
                  <LomavisPostCardList1 data={sortedPosts} />
                )}
              </div>
            </div>
          )
        ) : (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            {' '}
            <div
              style={{ minHeight: '500px' }}
              className="w-100 h-50 bg-white rounded p-2 d-flex flex-column gap-4 justify-content-center align-items-center"
            >
              <span className="fs-3">
                {t('lomavisCompetitorAnalysis.noCompetitor')}
              </span>
              <div className="d-flex gap-3 justify-content-center">
                {' '}
                {selectedPlatform && (
                  <LomavisPlatformIcon platform={selectedPlatform} size={40} />
                )}{' '}
              </div>
            </div>
          </div>
        )
      ) : (
        <div className="w-100 h-100 d-flex flex-column gap-2">
          <div className="row g-2">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="skeleton-loader h-400px rounded"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialFeeds;
